import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useLazyListVehiclesQuery } from "store/services/vehicleApi";
import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import { localizeDateShort } from "utils/dateFormats";
import { BaseTabs } from "components/Shared/BaseTabs";
// import ExportVehicleInsuranceExcel from "./ExportVehicleInsuranceExcel";

const VehicleInsuranceListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const breadcrumbs = [
    {
      title: "Reports",
      path: "/app/crm/reports/list",
    },
    {
      title: "Vehicle Insurances",
    },
  ];

  const vehicleInsuranceListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
    { label: "Expiring Soon", value: "Expiring Soon" },
    { label: "Expired", value: "Expired" },
  ];

  const [fetchVehicles, { data: vehicleRes, isLoading, isFetching, error }] =
    useLazyListVehiclesQuery();

  React.useEffect(() => {
    fetchVehicles({
      Active: "true",
      insuranceStatus: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchVehicles, keyword, perPage]);

  const columns = [
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicle_id,
      cell: (row) => row.vehicle_id,
    },
    {
      name: "Registration Number",
      selector: (row) => row.registration_number,
      cell: (row) => row.registration_number,
    },
    {
      name: "Insurance ID",
      selector: (row) => row.vehicleInsurance?.insurance_id,
      cell: (row) => row.vehicleInsurance?.insurance_id || "N/A",
    },
    {
      name: "Provider",
      selector: (row) => row.vehicleInsurance?.provider_name,
      cell: (row) => row.vehicleInsurance?.provider_name || "N/A",
    },
    {
      name: "Policy Number",
      selector: (row) => row.vehicleInsurance?.policy_number,
      cell: (row) => row.vehicleInsurance?.policy_number || "N/A",
    },
    {
      name: "Start Date",
      selector: (row) => row.vehicleInsurance?.start_date,
      cell: (row) =>
        row.vehicleInsurance?.start_date
          ? localizeDateShort(row.vehicleInsurance.start_date)
          : "N/A",
    },
    {
      name: "Expiry Date",
      selector: (row) => row.vehicleInsurance?.expiry_date,
      cell: (row) =>
        row.vehicleInsurance?.expiry_date
          ? localizeDateShort(row.vehicleInsurance.expiry_date)
          : "N/A",
    },
    {
      name: "Premium Amount",
      selector: (row) => row.vehicleInsurance?.premium_amount,
      cell: (row) =>
        row.vehicleInsurance?.premium_amount
          ? `Rs.${row.vehicleInsurance.premium_amount}`
          : "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/vehicle/${row._id}/detail?tab=insurance`}>
            <IconButton
              size="small"
              aria-label="View Insurance"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // Update the document title or app meta data using a custom hook
  useAppMeta(`${activeTab} Vehicle Insurances`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={vehicleInsuranceListTabs}
          defaultValue={activeTab}
          // actions={<ExportVehicleInsuranceExcel
          //   activeTab={activeTab}
          //    />
          // }
        />
        <TableFilter
          searchLabel="Vehicle ID/Registration Number"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"> {activeTab} Vehicle Insurances </div>
        </TableFilter>
        <AppTable
          columns={columns}
          data={vehicleRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={vehicleRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default VehicleInsuranceListScreen;
